import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";

const EASE = [0.76, 0, 0.24, 1];

export const Newsletter = () => {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) return;
    setSubmitted(true);
  };

  return (
    <section
      id="journal"
      data-testid="newsletter-section"
      className="relative bg-[color:var(--bt-bg-2)] border-t border-[color:var(--bt-gold-line)] py-40 md:py-64 px-6 md:px-10 overflow-hidden"
    >
      {/* Ornament */}
      <div
        aria-hidden
        className="absolute top-1/2 -translate-y-1/2 -right-40 md:-right-20 font-serif-display italic text-[45vw] md:text-[30vw] leading-none text-[color:var(--bt-gold)]/[0.045] select-none pointer-events-none"
      >
        RSVP
      </div>

      <div className="relative mx-auto max-w-[1400px] grid md:grid-cols-12 gap-10 md:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: EASE }}
          className="md:col-span-6"
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="h-px w-10 bg-[color:var(--bt-gold)]" />
            <span
              data-testid="newsletter-eyebrow"
              className="text-[11px] tracking-widest-brand uppercase text-[color:var(--bt-gold)]"
            >
              Chapter V · The Ledger
            </span>
          </div>
          <h2
            data-testid="newsletter-heading"
            className="font-serif-display text-5xl md:text-7xl leading-[0.98] tracking-tight text-[color:var(--bt-ink)]"
          >
            The evening{" "}
            <span className="italic font-light text-[color:var(--bt-gold)]">
              dispatch.
            </span>
          </h2>
          <p className="mt-6 text-[color:var(--bt-ink-dim)] text-base md:text-lg leading-relaxed max-w-lg">
            One quiet email each Thursday. New evenings, private allocations,
            and the occasional interview from behind the ballroom doors. No
            leaderboards. No noise.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
          className="md:col-span-6"
        >
          {!submitted ? (
            <form
              onSubmit={onSubmit}
              data-testid="newsletter-form"
              className="flex flex-col gap-6"
            >
              <div className="relative border-b border-[color:var(--bt-gold-line)] focus-within:border-[color:var(--bt-gold)] transition-colors">
                <label className="block text-[10px] tracking-widest-brand uppercase text-[color:var(--bt-ink-dim)] mb-2">
                  Your correspondence
                </label>
                <div className="flex items-center gap-4">
                  <input
                    data-testid="newsletter-email-input"
                    type="email"
                    required
                    placeholder="you@house.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-1 bg-transparent py-4 text-2xl md:text-3xl font-serif-display text-[color:var(--bt-ink)] placeholder:text-[color:var(--bt-ink-faint)] focus:outline-none"
                  />
                  <button
                    type="submit"
                    data-testid="newsletter-submit"
                    className="group flex items-center gap-3 px-6 py-3 border border-[color:var(--bt-gold)] bg-[color:var(--bt-gold)] text-[color:var(--bt-bg)] hover:bg-transparent hover:text-[color:var(--bt-gold)] transition-colors"
                  >
                    <span className="text-[10px] tracking-widest-brand uppercase">
                      Subscribe
                    </span>
                    <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                  </button>
                </div>
              </div>
              <div className="text-[10px] tracking-widest-brand uppercase text-[color:var(--bt-ink-dim)]">
                By subscribing you accept our terms - never shared, never sold.
              </div>
            </form>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="newsletter-success"
              className="border border-[color:var(--bt-gold)] p-8 flex items-start gap-4"
            >
              <Check
                size={22}
                className="text-[color:var(--bt-gold)] mt-1 flex-shrink-0"
              />
              <div>
                <p className="font-serif-display text-2xl text-[color:var(--bt-ink)]">
                  Consider yourself on the list.
                </p>
                <p className="text-sm text-[color:var(--bt-ink-dim)] mt-2">
                  Your first dispatch will arrive Thursday, before eight.
                </p>
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default Newsletter;
